import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createCategory, updateCategory } from "../../services/InventorySettings";
import { ICategoryRequestDTO } from "../../interfaces/InventorySettings";
import { formFieldStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditCategoryProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  typeId: number;
  onSuccess: (message: string) => void;
}

const AddEditCategory: React.FC<AddEditCategoryProps> = ({
  open,
  onClose,
  editData,
  typeId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    categoryName: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const COMPANY_ID = loggedInUser?.companyId;

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          categoryName: editData.categoryName || "",
        });
      } else {
        setFormData({
          categoryName: "",
        });
      }
      setErrors({});
    }
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.categoryName) newErrors.categoryName = "Category name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: ICategoryRequestDTO = {
        companyId: COMPANY_ID,
        categoryName: formData.categoryName,
        typeId: typeId,
      };

      const response = isEdit 
        ? await updateCategory(editData.id, dto)
        : await createCategory(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Category updated successfully" : "Category created successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || response?.errorResponse?.message || "Operation failed";
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err?.message || "An error occurred while saving");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5, pt: 1.5, px: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>{editData ? "Edit Category" : "Add Category"}</Typography>
          <Typography variant="body2" color="grey.500" sx={{ fontSize: "12px", mt: 0.3 }}>
            {editData ? "Edit Category" : "Add Category"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Category Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Smartphones"
              value={formData.categoryName}
              onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              error={!!errors.categoryName}
              helperText={errors.categoryName}
              sx={{...formFieldStyles}}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>{editData ? "Save" : "Add"}</Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditCategory;